import { AutogenLayoutPendingCommissions } from "./autogen/AutogenLayoutPendingCommissions";
import {
    AddType, ChangeEvent, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, LookupModelSearchEvent, LookupModelSelectionEvent, PrintableEvent
} from "@mcleod/components";
import { AutoMatchProfile } from "@mcleod/dispatch/src/AutoMatchProfile";
import { CommonTooltips, ImageTable } from "@mcleod/common";
import { Currency, FieldUpdateEvent, HorizontalAlignment } from "@mcleod/core";
import { ModelOrders, OrderPostHist, OrderPostHistProps } from "@mcleod/dispatch";
import { UsersResponsibilitySlideoutTrans } from "@mcleod/general";
import { MovementUsers } from "@mcleod/dispatch/src/MovementUsers";
import { ModelOffPayee } from "../../officepayroll/src/models/ModelOffPayee";

export class PendingCommissions extends AutogenLayoutPendingCommissions {
    onLoad(): void | Promise<void> {
        this.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                this.title = "Pending Commissions - " + this.titleSearchModeSuffix;
                this.tabStopDetails.visible = false;
                this.tabImages.visible = false
                this.textboxOrderId.lookupModelDisabled = true;
            }
            if (this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) {
                this.setComponentListeners();
                if (this.mainDataSource.mode == DataSourceMode.ADD) {
                    this.title = "Pending Commissions - New";
                } else {
                    const orderId = this.activeRow?.get("order_id");
                    const payeeName = this.textboxPayeeId.getFirstLookupModelData()?.get("name");
                    this.title = `Pending Commissions - Order ${orderId} - ${payeeName}`;
                    this.buttonResponsibility.visible = true;
                    this.buttonOrderHistory.visible = true;
                }

                this.activeRow.addAfterFieldUpdateListener(async (event: FieldUpdateEvent) => {
                    if (this.mainDataSource.isAdding || this.mainDataSource.isUpdating) return;
                    const commissionableMarginFields = ["linehaul_charge", "other_charges", "carrier_pay", "carrier_extra_pay"];
                    if (commissionableMarginFields.includes(event.fieldName)) {
                        const result = this.activeRow.getCurrency("linehaul_charge", new Currency(0));
                        await result.add(this.activeRow.getCurrency("other_charges"));
                        await result.subtract(this.activeRow.getCurrency("carrier_pay"));
                        await result.subtract(this.activeRow.getCurrency("carrier_extra_pay"));
                        this.activeRow.set("commissionable_margin", result);
                    }
                    if (event.fieldName === "commissionable_margin" || event.fieldName === "commissions_split_percent" || event.fieldName === "override_rate") {
                        const result = this.activeRow.getCurrency("commissionable_margin", new Currency(0));
                        result.multiply(this.activeRow.get("commissions_split_percent", 0) / 100);
                        this.activeRow.set("split_comm_margin", result);
                        const recurringExpense = this.activeRow.getCurrency("recurring_expense");
                        const allocatedDraw = this.activeRow.getCurrency("allocated_draw_amount");
                        let netCommissionableMargin = result;
                        if (this.activeRow.isNull("commissions_split_percent")) {
                            netCommissionableMargin = this.activeRow.getCurrency("commissionable_margin", new Currency(0));
                        }
                        await netCommissionableMargin.subtract(recurringExpense);
                        await netCommissionableMargin.subtract(allocatedDraw);
                        this.activeRow.set("net_commissionable_margin", netCommissionableMargin);
                    }
                    if (event.fieldName === "net_commissionable_margin" || event.fieldName === "pay_rate") {
                        const result = this.activeRow.getCurrency("net_commissionable_margin", new Currency(0));
                        result.multiply(this.activeRow.get("pay_rate", 0) / 100);
                        this.activeRow.set("total_pay", result);
                    }
                });
            }
        });
        this.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() == DataSourceAction.SEARCH) {
                (this.layoutImageTable as ImageTable).populateForRow("O", this.activeRow.get("order_id"));
                if (!this.activeRow.isNull("movement_id")) {
                    CommonTooltips.setTooltipContentLayout(this.textboxOrderId, this.activeRow.get("movement_id"), "lme/settlement/OrderPayablesOrderIdQuickInfo");
                }
            }
        });
        this.mainDataSource.addBeforeExecutionListener(async (event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.UPDATE) {
                if (!this.activeRow.isNull("process_status")) {
                    this.activeRow.set("process_status", null);
                    const offPayeeRow = await new ModelOffPayee().searchSingle({ id: this.activeRow.get("payee_id") });
                    if (offPayeeRow != null) {
                        offPayeeRow.set("commission_status", null);
                        await offPayeeRow.post();
                    }
                }
            }
        });
        this.textboxPayeeId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter["off_payee.enable_commissions_pay"] = "Y";
        });
    }

    private setComponentListeners() {
        this.textboxOrderId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            if (this.textboxOrderId.text?.length < this.textboxOrderId.lookupModelMinChars) {
                event.preventDefault();
            } else {
                event.filter.order_id = this.textboxOrderId.text;
                event.filter.lm_search = this.textboxOrderId.text;
            }
        });
        this.textboxMovementId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            if (this.textboxOrderId.text?.length < this.textboxOrderId.lookupModelMinChars) {
                event.preventDefault();
            } else {
                event.filter.order_id = this.textboxOrderId.text;
                event.filter.lm_search = this.textboxOrderId.text;
            }
        });
        this.textboxOrderId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.handleMovementLookupSelection(event);
        });
        this.textboxMovementId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.handleMovementLookupSelection(event);
        });
        this.checkboxOverrideRate.addChangeListener((event: ChangeEvent) => {
            if (event.newValue) {
                this.activeRow.setValueAndLookupModelData("rate_code", null, null);
                this.activeRow.set("rate_type", null);
                this.activeRow.set("recurring_expense", null);
                this.activeRow.set("allocated_draw_amount", null);
                this.activeRow.set("payee_draw_amount", null);
                this.activeRow.set("override_rate", event.newValue); // re-set override rate to true to trigger field updates
            }
        });
    }

    private handleMovementLookupSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;

            // Movement fields
            this.activeRow.setValueAndLookupModelData("movement_id", selection.get("id"), { id: selection.get("id") });
            this.activeRow.set("manifest_id", selection.get("manifest_id"));

            // Order fields
            this.activeRow.set("commodity_id", selection.get("orders.commodity_id"));
            if (!this.activeRow.isNull("commodity_id")) {
                AutoMatchProfile.applyLookupModelData(this.textboxCommodityId, this.activeRow).then(() => {
                    this.textboxCommodityId.displayComponentData(this.activeRow, null, 0);
                });
            }
            this.activeRow.set("revenue_id", selection.get("orders.revenue_code_id"));
            if (!this.activeRow.isNull("revenue_id")) {
                AutoMatchProfile.applyLookupModelData(this.textboxRevenueId, this.activeRow).then(() => {
                    this.textboxRevenueId.displayComponentData(this.activeRow, null, 0);
                });
            }
            this.activeRow.set("order_type", selection.get("orders.order_type_id"));
            if (!this.activeRow.isNull("order_type")) {
                AutoMatchProfile.applyLookupModelData(this.textboxOrderType, this.activeRow).then(() => {
                    this.textboxOrderType.displayComponentData(this.activeRow, null, 0);
                });
            }
            this.activeRow.set("customer_id", selection.get("orders.customer_id"));
            if (!this.activeRow.isNull("customer_id")) {
                AutoMatchProfile.applyLookupModelData(this.textboxCustomerId, this.activeRow).then(() => {
                    this.textboxCustomerId.displayComponentData(this.activeRow, null, 0);
                });
            }

            // Stop fields
            this.activeRow.set("origin_city", selection.get("stop_origin.city_name"));
            this.activeRow.setValueAndLookupModelData("origin_state", selection.get("stop_origin.state"), { "id": selection.get("stop_origin.state") });
            this.activeRow.set("destination_city", selection.get("stop_dest.city_name"));
            this.activeRow.setValueAndLookupModelData("destination_state", selection.get("stop_dest.state"), { "id": selection.get("stop_dest.state") });
            this.activeRow.set("ship_date", selection.get("stop_origin.sched_arrive_early"));
            this.activeRow.set("delivery_date", selection.get("stop_dest.sched_arrive_early"));
            this.sourceStop.search({ movement_id: this.activeRow.get("movement_order.movement_id") });
            this.sourceOrderOtherCharge.search({ order_id: this.activeRow.get("order_id") });

            CommonTooltips.setTooltipContentLayout(this.textboxOrderId, this.activeRow.get("movement_id"), "lme/settlement/OrderPayablesOrderIdQuickInfo");
        }
    }

    textboxNetCommissionableMarginOnPrintable(event: PrintableEvent) {
        const label = event.printableLabel;
        if (label != null) {
            label.align = HorizontalAlignment.LEFT;
        }
    }

    textboxRateOnPrintable(event: PrintableEvent) {
        const label = event.printableLabel;
        if (label != null) {
            label.align = HorizontalAlignment.LEFT;
        }
    }

    textboxTotalPayOnPrintable(event: PrintableEvent) {
        const label = event.printableLabel;
        if (label != null) {
            label.align = HorizontalAlignment.LEFT;
        }
    }

    async buttonOrderHistoryOnClick(event: ClickEvent) {
        this.buttonOrderHistory.busy = true;
        const orderRow = await new ModelOrders().searchSingle({ id: this.activeRow.get("order_id") }, "lme/dispatch/Orders");
        const props: OrderPostHistProps = {
            orderType: "ORDER",
            orderId: this.activeRow.get("order_id"),
            movementId: this.activeRow.get("movement_id"),
            isBilled: !orderRow.isNull("bill_date") || !orderRow.isNull("xferred2billing_dt"),
            allowDelete: false
        };
        OrderPostHist.showInSlideout(
            props, DataSourceMode.UPDATE, this.buttonOrderHistory);
        this.buttonOrderHistory.busy = false;
    }

    buttonResponsibilityOnClick(event: ClickEvent) {
        const urs = new UsersResponsibilitySlideoutTrans("lme/dispatch/MovementUsers", "Movement Users - " + this.activeRow.get("movement_id"), { id: this.activeRow.get("movement_id") }, [], this.sourceCommission, this.sourceResponsibleHist);
        const responsibleHistLayout = urs.layout as MovementUsers;
        responsibleHistLayout.addLayoutLoadListener(() => {
            responsibleHistLayout.textboxDispatcherUserId.visible = false;
            responsibleHistLayout.textboxFleetManager.visible = false;
            responsibleHistLayout.textboxOperationsUser.visible = false;
            responsibleHistLayout.layoutResponsibleHist.tableResponsibleHist.addType = AddType.NOT_ALLOWED;
            responsibleHistLayout.layoutResponsibleHist.tableResponsibleHist.allowEdit = false;
            responsibleHistLayout.layoutResponsibleHist.tableResponsibleHist.allowDelete = false;
            urs.includeCommissions = true;
        });
        urs.show();
    }
}
